import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { FilterPopoverRangePane } from 'features/charts-v2/components/FilterPopoverRangePane/FilterPopoverRangePane'
import {
  POPOVER_SHADOW,
  useFixedPopoverPlacement,
} from 'features/charts-v2/hooks/use-popover-placement'
import { useEscapeDismiss } from 'hooks/use-escape-dismiss'

// Placement (`style`) comes from useFixedPopoverPlacement; the rest is static.
const PopoverSurface = styled.div<{ $shadow: string; $measured: boolean }>`
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: ${({ $shadow }) => $shadow};
  z-index: 2000;
  visibility: ${({ $measured }) => ($measured ? 'visible' : 'hidden')};
`

type FilterPopoverRangePaneProps = React.ComponentProps<
  typeof FilterPopoverRangePane
>

interface FilterRangePopoverProps extends FilterPopoverRangePaneProps {
  isOpen: boolean
  onClose: () => void
  cardRef?: React.RefObject<HTMLElement>
}

// A filter card's min/max editor for a numeric custom field. Same
// card-anchored shell as RatingTypePopover: portal-rendered, `position: fixed`,
// placed by useFixedPopoverPlacement.
export const FilterRangePopover: React.FC<FilterRangePopoverProps> = ({
  isOpen,
  onClose,
  cardRef,
  ...paneProps
}) => {
  const popoverRef = useRef<HTMLDivElement>(null)
  const { placement, isMeasured, style } = useFixedPopoverPlacement({
    anchorRef: cardRef,
    popoverRef,
    isOpen,
  })

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        isOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        cardRef?.current &&
        !cardRef.current.contains(target)
      ) {
        onClose()
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose, cardRef])

  useEscapeDismiss({ isOpen, onClose })

  if (!isOpen) return null

  return createPortal(
    <PopoverSurface
      ref={popoverRef}
      data-yogi-overlay="true"
      $shadow={POPOVER_SHADOW[placement]}
      $measured={isMeasured}
      style={style}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <FilterPopoverRangePane
        {...paneProps}
        onApply={(range) => {
          paneProps.onApply(range)
          onClose()
        }}
        onClear={() => {
          paneProps.onClear()
          onClose()
        }}
      />
    </PopoverSurface>,
    document.body,
  )
}
